// src/Sidebar.js - Updated for dynamic step coloring and icons
import { useEffect, useRef, useState } from 'react';
import efsLogo from './assets/efs-logo-navy.svg';

const steps = [
  'Report Details',
  'Event Information',
  'Ticketing Issues',
  'Carparking Issues',
  'Corporate Room/Suite Issues',
  'Cleaning/Maintenance Issues',
  'WHS Issues',
  'Other Operational Issues',
  'Incidents',
  'Additional Taskings',
  'Service Complaints',
  'Staff Feedback/Staffing Issues',
  'Sign off & Submit'
];

const Sidebar = ({
  currentStep = 1,
  stepValidation = {},
  visitedSteps = new Set([1]),
  onStepClick
}) => {
  const [showLeftChevron, setShowLeftChevron] = useState(false);
  const [showRightChevron, setShowRightChevron] = useState(false);
  const scrollContainerRef = useRef(null);

  // Debug logging
  useEffect(() => {
    console.log('Sidebar re-rendered:', { 
      currentStep, 
      visitedStepsArray: Array.from(visitedSteps),
      stepValidation 
    });
  }, [currentStep, visitedSteps, stepValidation]);

  const getStepStatus = (stepNumber) => {
    // Always show current step as 'current', regardless of visited status
    if (stepNumber === currentStep) {
      return 'current';
    }
    // Only check visited/validation for steps that are NOT current
    if (visitedSteps.has(stepNumber) && stepNumber < currentStep) {
      if (stepValidation.hasOwnProperty(stepNumber)) {
        return stepValidation[stepNumber] ? 'valid' : 'invalid';
      }
      // assume valid if visited but no explicit validation
      return 'valid';
    }
    return 'future';
  };

  const getStepIcon = (status, stepNumber) => {
    // Only show checkmark/X for completed (not current) steps
    if (status === 'valid') return '✓';
    if (status === 'invalid') return '✗';
    // Current step and future steps show the step number
    return stepNumber;
  };

  const handleStepClick = (stepNumber, status) => {
    // clickable unless it's a future step
    if (status !== 'future' && onStepClick) {
      onStepClick(stepNumber);
    }
  };

  // update chevrons on scroll/resize
  const checkScrollPosition = () => {
    const c = scrollContainerRef.current;
    if (c) {
      setShowLeftChevron(c.scrollLeft > 0);
      setShowRightChevron(c.scrollLeft < c.scrollWidth - c.clientWidth - 1);
    }
  };

  const scrollLeft = () => {
    scrollContainerRef.current?.scrollBy({ left: -150, behavior: 'smooth' });
  };
  const scrollRight = () => {
    scrollContainerRef.current?.scrollBy({ left: 150, behavior: 'smooth' });
  };

  useEffect(() => {
    const c = scrollContainerRef.current;
    if (c) {
      checkScrollPosition();
      c.addEventListener('scroll', checkScrollPosition);
      window.addEventListener('resize', checkScrollPosition);
      return () => {
        c.removeEventListener('scroll', checkScrollPosition);
        window.removeEventListener('resize', checkScrollPosition);
      };
    }
  }, []);

  return (
    <div className="sidebar" style={{
      background: 'linear-gradient(180deg, #1B3B6F 0%, #2A4A7F 100%)',
      borderRight: '1px solid rgba(255, 255, 255, 0.1)'
    }}>
      <div className="logo" style={{
        marginBottom: '2.5rem',
        textAlign: 'left',
        paddingLeft: '15px',
        marginTop: '10px'
      }}>
        <img
          src={efsLogo}
          alt="efs Logo"
          style={{
            width: 'clamp(160px, 20vw, 320px)',
            height: 'auto',
            display: 'block',
            margin: 0
          }}
        />
      </div>

      {/* Desktop steps */}
      <ul className="steps-list desktop-steps" style={{
        listStyle: 'none',
        padding: 0,
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '1.2rem'
      }}>
        {steps.map((label, idx) => {
          const stepNumber = idx + 1;
          const status = getStepStatus(stepNumber);
          const isClickable = status !== 'future';

          // Updated colors for navy sidebar background
          const labelColor = status === 'current'
            ? '#F4C430'  // Yellow for current (brand CTA color)
            : status === 'future'
              ? 'rgba(255, 255, 255, 0.4)'  // Faded white for future
              : '#FFFFFF';  // White for completed/valid

          const borderColor = status === 'current'
            ? '#F4C430'  // Yellow border for current
            : status === 'valid'
              ? '#22c55e'  // Green for valid
              : status === 'invalid'
                ? '#ef4444'  // Red for invalid
                : 'rgba(255, 255, 255, 0.3)';  // Subtle white for future

          const bgColor = status === 'current'
            ? '#F4C430'  // Yellow background for current
            : status === 'valid'
              ? '#22c55e'  // Green background for valid
              : status === 'invalid'
                ? '#ef4444'  // Red background for invalid
                : 'transparent';

          const numberColor = (status === 'current' || status === 'valid' || status === 'invalid')
            ? '#1B3B6F'  // Navy text on colored backgrounds
            : 'rgba(255, 255, 255, 0.5)';  // Faded white for future

          return (
            <li
              key={label}
              className={`step ${status}`}
              onClick={() => handleStepClick(stepNumber, status)}
              style={{
                cursor: isClickable ? 'pointer' : 'default',
                display: 'flex',
                alignItems: 'center',
                fontSize: '1rem',
                fontWeight: status === 'current' ? '600' : '500',
                color: labelColor,
                transition: 'all 0.2s ease',
                padding: '8px 4px',
                borderRadius: '6px',
                marginBottom: 0
              }}
              onMouseEnter={e => {
                if (isClickable) {
                  e.currentTarget.style.backgroundColor = 'rgba(244, 196, 48, 0.15)';  // Yellow highlight
                  e.currentTarget.style.transform = 'translateX(4px)';
                }
              }}
              onMouseLeave={e => {
                if (isClickable) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.transform = 'translateX(0)';
                }
              }}
            >
              <div className="step-number" style={{
                width: '2rem',
                height: '2rem',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px',
                fontSize: '0.85rem',
                fontWeight: '600',
                border: `2px solid ${borderColor}`,
                backgroundColor: bgColor,
                color: numberColor,
                transition: 'all 0.2s ease',
                flexShrink: 0
              }}>
                {getStepIcon(status, stepNumber)}
              </div>
              <span style={{ lineHeight: '1.4', flex: 1 }}>
                {label}
              </span>
            </li>
          );
        })}
      </ul>

      {/* Mobile steps (hidden by default, shown via CSS media queries) */}
      <div className="mobile-steps-container" style={{ display: 'none' }}>
        {showLeftChevron && (
          <button
            onClick={scrollLeft}
            aria-label="Scroll left"
            style={{
              background: 'rgba(244, 196, 48, 0.2)',  // Yellow accent
              border: '1px solid rgba(244, 196, 48, 0.4)',
              color: '#F4C430',
              borderRadius: '50%',
              width: '2.5rem',
              height: '2.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '1.2rem',
              fontWeight: 'bold',
              marginRight: '0.8rem',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'rgba(244, 196, 48, 0.3)';
              e.currentTarget.style.borderColor = 'rgba(244, 196, 48, 0.6)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'rgba(244, 196, 48, 0.2)';
              e.currentTarget.style.borderColor = 'rgba(244, 196, 48, 0.4)';
            }}
          >‹</button>
        )}
        <ul
          ref={scrollContainerRef}
          className="steps-list mobile-steps"
          style={{
            display: 'flex',
            flexDirection: 'row',
            overflowX: 'auto',
            scrollBehavior: 'smooth',
            gap: '1rem',
            padding: '0.8rem 0',
            margin: 0,
            listStyle: 'none',
            flex: 1,
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
        >
          {steps.map((label, idx) => {
            const stepNumber = idx + 1;
            const status = getStepStatus(stepNumber);
            const isClickable = status !== 'future';

            // Mobile step colors (same as desktop, adapted for navy background)
            const labelColor = status === 'current'
              ? '#F4C430'  // Yellow
              : status === 'future'
                ? 'rgba(255, 255, 255, 0.4)'
                : '#FFFFFF';

            const borderColor = status === 'current'
              ? '#F4C430'
              : status === 'valid'
                ? '#22c55e'
                : status === 'invalid'
                  ? '#ef4444'
                  : 'rgba(255, 255, 255, 0.3)';

            const bgColor = status === 'current'
              ? '#F4C430'
              : status === 'valid'
                ? '#22c55e'
                : status === 'invalid'
                  ? '#ef4444'
                  : 'transparent';

            const numberColor = (status === 'current' || status === 'valid' || status === 'invalid')
              ? '#1B3B6F'  // Navy on colored backgrounds
              : 'rgba(255, 255, 255, 0.5)';

            return (
              <li
                key={label}
                className={`step ${status}`}
                onClick={() => handleStepClick(stepNumber, status)}
                style={{
                  cursor: isClickable ? 'pointer' : 'default',
                  flexDirection: 'column',
                  textAlign: 'center',
                  minWidth: '6rem',
                  fontSize: '0.8rem',
                  flexShrink: 0,
                  padding: '0.4rem',
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: status === 'current' ? '600' : '500',
                  color: labelColor,
                  transition: 'all 0.2s ease'
                }}
              >
                <div className="step-number" style={{
                  width: '1.8rem',
                  height: '1.8rem',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '0.4rem',
                  fontSize: '0.7rem',
                  fontWeight: '600',
                  border: `2px solid ${borderColor}`,
                  backgroundColor: bgColor,
                  color: numberColor,
                  transition: 'all 0.2s ease'
                }}>
                  {getStepIcon(status, stepNumber)}
                </div>
                <span style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '5.5rem',
                  lineHeight: '1.2',
                  fontSize: '0.75rem'
                }}>
                  {label}
                </span>
              </li>
            );
          })}
        </ul>
        {showRightChevron && (
          <button
            onClick={scrollRight}
            aria-label="Scroll right"
            style={{
              background: 'rgba(244, 196, 48, 0.2)',
              border: '1px solid rgba(244, 196, 48, 0.4)',
              color: '#F4C430',
              borderRadius: '50%',
              width: '2.5rem',
              height: '2.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '1.2rem',
              fontWeight: 'bold',
              marginLeft: '0.8rem',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'rgba(244, 196, 48, 0.3)';
              e.currentTarget.style.borderColor = 'rgba(244, 196, 48, 0.6)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'rgba(244, 196, 48, 0.2)';
              e.currentTarget.style.borderColor = 'rgba(244, 196, 48, 0.4)';
            }}
          >›</button>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
